import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { Theme1BackgroundOverlay } from '@/components/Theme1BackgroundOverlay';
import PublicHeader from './components/Header/PublicHeader';
import PublicFooter from './components/Footer/PublicFooter';
import BottomNav from '@/components/BottomNav/BottomNav';
import { PostLoginTourGate } from '@/components/TourGuide';

interface PublicLayoutProps {
  header?: React.ReactNode | 'default' | 'none';
  footer?: React.ReactNode | 'default' | 'none';
  bodyClassName?: string;
  showHeader?: boolean;
}

/** Help and its ticket view (the old /faq and /support URLs redirect there). */
function isCosmosSupportPath(pathname: string): boolean {
  return pathname === '/help' || pathname.startsWith('/help/');
}

/**
 * Layout component for public pages (Home, Help, etc.)
 * Help uses the same cosmos background shell as authenticated account pages.
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({
  header = 'default',
  footer = 'none',
  bodyClassName = '',
  showHeader = true,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const isHome = pathname === '/';
  const isCosmosShell = isCosmosSupportPath(pathname);
  const useCosmosBackground = isHome || isCosmosShell;
  const fullScreenBg = usePageFullScreenBackground();

  const headerNode =
    showHeader && header !== 'none'
      ? header === 'default'
        ? isHome || isCosmosShell ? (
            <PublicHeader compact={isHome || isCosmosShell} />
          ) : (
            <PublicHeader />
          )
        : header
      : null;

  const footerNode =
    footer === 'none' ? null : footer === 'default' ? (
      <div className="relative z-10 shrink-0">
        <PublicFooter />
      </div>
    ) : (
      footer
    );

  if (useCosmosBackground) {
    // Drop hook `min-h-screen` — parent is h-dvh; 100vh on Android Chrome is often
    // taller than the visible viewport and clips BottomNav below the fold.
    const shellBgClass = fullScreenBg.className.replace(/\bmin-h-screen\b/g, '').trim();
    return (
      <div
        className={`relative z-1 ${shellBgClass} flex flex-col flex-1 min-h-0 h-full max-h-full w-full overflow-hidden`}
      >
        <Theme1BackgroundOverlay />
        {headerNode != null ? (
          <div className="wf-header-chrome safe-area-top shrink-0" role="banner">
            {headerNode}
          </div>
        ) : null}
        <main
          className={`relative z-1 flex-1 min-h-0 flex flex-col overflow-hidden ${bodyClassName}`}
        >
          {isCosmosShell ? (
            <div className="flex-1 min-h-0 cosmos-scroll-y overflow-x-hidden">
              <Outlet />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
        {isCosmosShell ? footerNode : null}
        <BottomNav />
        <PostLoginTourGate />
      </div>
    );
  }

  return (
    <div className="safe-area-top flex flex-col flex-1 min-h-0 w-full overflow-hidden">
      {headerNode != null ? <div className="shrink-0">{headerNode}</div> : null}
      <main className={`flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden cosmos-scroll-y ${bodyClassName}`}>
        <Outlet />
      </main>
      {footerNode}
      <BottomNav />
      <PostLoginTourGate />
    </div>
  );
};

export default PublicLayout;
